/**
 * useRequestPriorityThresholds
 *
 * Returns the configurable day-thresholds that map a resource request's
 * requested start date to a priority level (critical / high / medium / low).
 *
 * Thresholds are stored in AppSettings.requestPriorityThresholds and fall
 * back to sensible defaults when not configured.
 */

import { useMemo } from 'react';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { useScheduler } from '@/contexts/SchedulerContext';
import type { RequestPriority } from '@/types/resourceRequest';

export interface RequestPriorityThresholds {
  criticalDays: number; // ≤ N days  → critical
  highDays:     number; // ≤ N days  → high
  mediumDays:   number; // ≤ N days  → medium
  // > mediumDays → low
}

export const DEFAULT_PRIORITY_THRESHOLDS: RequestPriorityThresholds = {
  criticalDays: 7,
  highDays:     14,
  mediumDays:   30,
};

export function useRequestPriorityThresholds(): RequestPriorityThresholds {
  const { settings } = useScheduler();

  return useMemo<RequestPriorityThresholds>(() => ({
    criticalDays: settings?.requestPriorityThresholds?.criticalDays ?? DEFAULT_PRIORITY_THRESHOLDS.criticalDays,
    highDays:     settings?.requestPriorityThresholds?.highDays     ?? DEFAULT_PRIORITY_THRESHOLDS.highDays,
    mediumDays:   settings?.requestPriorityThresholds?.mediumDays   ?? DEFAULT_PRIORITY_THRESHOLDS.mediumDays,
  }), [settings?.requestPriorityThresholds]);
}

/**
 * Pure function — compute priority from a start date and a threshold config.
 * "Days until start" is measured from today (calendar days, not business days).
 * Negative values (already past the start date) always → critical.
 */
export function computeRequestPriority(
  requestedStartDate: string,
  thresholds: RequestPriorityThresholds,
): RequestPriority {
  const days = differenceInCalendarDays(parseISO(requestedStartDate), new Date());
  if (days <= thresholds.criticalDays) return 'critical';
  if (days <= thresholds.highDays)     return 'high';
  if (days <= thresholds.mediumDays)   return 'medium';
  return 'low';
}
